import { useMemo } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import { colors } from "theme/palette";

import { useDealChecklistPermission } from "entities/Deal/sdk";
import { EFieldStatus } from "entities/Field/constants";
import FieldStatusIcon from "entities/Package/components/FieldStatusIcon";
import { IPackageTab } from "entities/Package/sdk";

export const useChecklistTabProgress = ({ tab }: { tab?: IPackageTab }) => {
  const params = useParams<{
    dealId: string;
    packageId: string;
  }>();

  const dealId = Number(params.dealId);
  const packageId = Number(params.packageId);

  const { data: userPermission } = useDealChecklistPermission({
    dealId,
    packageId,
  });
  const hasWriteAccess = useMemo(
    () => !_.isNil(userPermission) && userPermission.has_write_access,
    [userPermission]
  );

  const [satisfiedCount, inReviewCount, doneCount, notDoneCount, naCount] =
    useMemo(() => {
      if (_.isNil(tab)) {
        return [0, 0, 0, 0, 0];
      }

      let satisfiedCount = 0;
      let inReviewCount = 0;
      let doneCount = 0;
      let notDoneCount = 0;
      let naCount = 0;

      tab.categories.forEach((category) => {
        category.sections.forEach((section) => {
          section.fields.forEach((field) => {
            switch (field.status) {
              case EFieldStatus.SATISFIED:
                satisfiedCount++;
                break;
              case EFieldStatus.IN_REVIEW:
                inReviewCount++;
                break;
              case EFieldStatus.DONE:
                doneCount++;
                break;
              case EFieldStatus.NOT_DONE:
                notDoneCount++;
                break;
              case EFieldStatus.NA:
                naCount++;
                break;
              default:
                break;
            }
          });
        });
      });

      return [satisfiedCount, inReviewCount, doneCount, notDoneCount, naCount];
    }, [tab]);

  const progressData = useMemo(() => {
    if (_.isNil(tab) || _.isNil(userPermission) || !hasWriteAccess) {
      return [];
    }

    // N/A are not included on purpose.
    const totalCount =
      satisfiedCount + inReviewCount + doneCount + notDoneCount;

    if (userPermission.is_checklist_owner) {
      return [
        {
          label: EFieldStatus.SATISFIED,
          percentage: (satisfiedCount / totalCount) * 100,
          color: colors.blue120,
          icon: <FieldStatusIcon status={EFieldStatus.SATISFIED} />,
        },
        {
          label: EFieldStatus.IN_REVIEW,
          percentage: (inReviewCount / totalCount) * 100,
          color: colors.blue80,
          icon: <FieldStatusIcon status={EFieldStatus.IN_REVIEW} />,
        },
        {
          label: EFieldStatus.DONE,
          percentage: (doneCount / totalCount) * 100,
          color: colors.blue100,
          icon: <FieldStatusIcon status={EFieldStatus.DONE} />,
        },
        {
          label: EFieldStatus.NOT_DONE,
          percentage: (notDoneCount / totalCount) * 100,
          color: colors.blue60,
          icon: <FieldStatusIcon status={EFieldStatus.NOT_DONE} />,
        },
      ];
    }

    return [
      {
        label: EFieldStatus.DONE,
        percentage:
          ((doneCount + inReviewCount + satisfiedCount) / totalCount) * 100,
        color: colors.blue100,
        icon: <FieldStatusIcon status={EFieldStatus.DONE} />,
      },
      {
        label: EFieldStatus.NOT_DONE,
        percentage: (notDoneCount / totalCount) * 100,
        color: colors.blue60,
        icon: <FieldStatusIcon status={EFieldStatus.NOT_DONE} />,
      },
    ];
  }, [
    tab,
    userPermission,
    hasWriteAccess,
    satisfiedCount,
    inReviewCount,
    doneCount,
    notDoneCount,
  ]);

  return {
    progressData,
    satisfiedCount,
    inReviewCount,
    doneCount,
    notDoneCount,
    naCount,
  };
};
